import { Controller } from "@hotwired/stimulus"; import { createRoot } from 'react-dom/client';
import React from "react";

import { StorefrontLoader } from "../components/storefront_loader";
import { FulfillmentMethod } from "../types/catalog";
import { Temporal } from "@js-temporal/polyfill";
import { StorefrontConfig, StoreInfo } from "../types/storefront";
import { StorefrontExperience } from "src/storefront/types/storefront_experience";
import { CartItemEventItemDefaults } from "src/analytics/cart_item_event_serializer";
import { StoreTypeRestrictions } from "src/storefront/types/store_type_restrictions";

export default class CatalogController extends Controller {
  declare stackableTargets: HTMLElement[];

  connect() : void {
    const csrfToken : string = document.querySelector("[name='csrf-token']")?.getAttribute("content") || "";
    const storefrontId : string | null = this.data.get("storefront-id");
    const storefrontName : string | null = this.data.get("storefront-name");
    const storefrontPermalink : string | null = this.data.get("storefront-permalink");
    const catalogId : string | null = this.data.get("id");
    const catalogUrl : string | null = this.data.get("url");
    const presentationStatus: string | null = this.data.get("presentation-status");
    const outageUrl : string | undefined = this.data.get("out-of-stock-list-url") || undefined;
    const fulfillmentMethodRaw = this.data.get("fulfillment-method");
    const rawTimeOffset : string | undefined = this.data.get("time-offset") || undefined;
    const rawPreferredTime : string | undefined = this.data.get("preferred-time") || undefined;
    const currentAddress : string = this.data.get("current-address") || "";
    const allowsMealPreferences : string | undefined = this.data.get('allows-meal-preferences') || undefined;
    const supportedFulfillmentMethods : Array<FulfillmentMethod> = JSON.parse(this.data.get("supported-fulfillment-methods") || '[]');
    const minimumSubtotal = Number(this.data.get("minimum-subtotal"));
    const rawStoreInfo = this.data.get("info");
    const rawStoreInfoParsed = rawStoreInfo ? JSON.parse(rawStoreInfo) : undefined;
    const ageVerifiedStatus = this.data.get('age-verified-status') === 'true';
    const usesNewBottomNav = this.data.get('uses-new-bottom-nav') === 'true';
    const usesSubcategoryDisplay = this.data.get('subcategory-display') === 'true';
    const usesProductSearch = this.data.get('product-search') === 'true';
    const showSubstitutionsDisclaimer = this.data.get('show-substitutions-disclaimer') === 'true';
    const allowSubstitutionPreferences = this.data.get('allow-substitution-preferences') === 'true';
    const hideAgeRestrictedItems = this.data.get('hide-age-restricted-items') === 'true';
    const hideOutOfStockItems = this.data.get('hide-out-of-stock-items') === 'true';
    const searchIndexName = this.data.get('search-index-name');
    const searchAppId = this.data.get('search-app-id');
    const searchPublicApiKey = this.data.get('search-public-api-key');
    const searchCustomerToken = this.data.get('search-customer-token');
    const searchAuthenticatedUserToken = this.data.get('search-authenticated-user-token');
    const storefrontExperience: StorefrontExperience = JSON.parse(this.data.get("storefront-experience") || '{}');
    const storeTypeRestrictions: StoreTypeRestrictions = JSON.parse(this.data.get("store-type-restrictions") || '{}');
    const defaultItemsEventAttributes: CartItemEventItemDefaults = JSON.parse(this.data.get("default-items-event-attributes") || '{}');

    if (fulfillmentMethodRaw != "delivery" && fulfillmentMethodRaw != "pickup") {
      throw "Missing fullfillment type information";
    }

    const fulfillmentMethod : FulfillmentMethod = fulfillmentMethodRaw;

    const storeInfo = new StoreInfo({
      cartFulfillmentTimeEstimate: rawStoreInfoParsed.cartFulfillmentTimeEstimate,
      fulfillmentTimeOptions: rawStoreInfoParsed.fulfillmentTimeOptions.map((x: string) => Temporal.Now.zonedDateTimeISO("Pacific/Auckland").withPlainTime(Temporal.PlainTime.from(x, { overflow: 'reject' }))),
    });

    if (!storefrontId || !storefrontName || !storefrontPermalink || !catalogId || !catalogUrl
      || !rawTimeOffset || !presentationStatus || !storeInfo
      || !searchIndexName || !searchAppId || !searchPublicApiKey || !searchCustomerToken
    )
      throw "Missing catalog rendering configuration";

    const timeOffset = Number.parseInt(rawTimeOffset);

    const parsePreferredTime = () => {
      try {
        if (!rawPreferredTime) return null;

        const preferredTime = Temporal.PlainTime.from(rawPreferredTime, { overflow: 'reject' });
        return Temporal.Now.zonedDateTimeISO("Pacific/Auckland").withPlainTime(preferredTime);
      } catch (err) {
        if (err instanceof RangeError) {
          return null;
        }
        throw err;
      }
    };

    const zonedPreferredTime = parsePreferredTime();

    const config = new StorefrontConfig({
      storefrontId: storefrontId,
      storefrontName: storefrontName,
      storefrontPermalink: storefrontPermalink,
      csrfToken: csrfToken,
      fulfillmentMethod: fulfillmentMethod,
      presentationStatus: presentationStatus,
      timeOffset: timeOffset,
      preferredTime: zonedPreferredTime,
      currentAddress: currentAddress,
      allowsMealPreferences: allowsMealPreferences === "true",
      supportedFulfillmentMethods: supportedFulfillmentMethods,
      minimumSubtotal: minimumSubtotal,
      storeInfo: storeInfo,
      usesNewBottomNav: usesNewBottomNav,
      usesProductSearch: usesProductSearch,
      usesSubcategoryDisplay: usesSubcategoryDisplay,
      showSubstitutionsDisclaimer: showSubstitutionsDisclaimer,
      allowSubstitutionPreferences: allowSubstitutionPreferences,
      priceFormatter: new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }),
      searchIndexName: searchIndexName,
      searchAppId: searchAppId,
      searchPublicApiKey: searchPublicApiKey,
      searchCustomerToken: searchCustomerToken,
      searchAuthenticatedUserToken: searchAuthenticatedUserToken || undefined,
      catalogUrl: catalogUrl,
      outageUrl: outageUrl,
      hideAgeRestrictedItems: hideAgeRestrictedItems,
      hideOutOfStockItems: hideOutOfStockItems,
      storefrontExperience: storefrontExperience,
      storeTypeRestrictions: storeTypeRestrictions,
      defaultItemsEventAttributes: defaultItemsEventAttributes
    });

    const root = createRoot(this.element);
    root.render(React.createElement(StorefrontLoader, {
      config: config,
      catalogId: catalogId,
      ageVerifiedStatus: ageVerifiedStatus,
    }));
  }
}
